import React, { useState, useEffect } from 'react';
import PublicLayout from '../components/PublicLayout';
import Seo from '../components/Seo';
import { demandAPI } from '../services/api';
import { ProductListSkeleton, EmptyState } from '../components/skeletons';
import { MapPin } from 'lucide-react';

const C = {
  green: '#1B5C3A', greenDark: '#0F3D26', greenLight: '#E8F5EE',
  gold: '#F5A623', goldLight: '#FFF8E7', goldDark: '#C4831A',
  cream: '#FAF8F3', charcoal: '#1A1A1A', muted: '#6B7280',
  border: '#E5E7EB', grayLight: '#F3F4F6', white: '#FFFFFF',
};
const F = { display: "'Fraunces', Georgia, serif", body: "'Plus Jakarta Sans', sans-serif" };

const capitalizeCity = (str) => {
  if (!str) return 'Other';
  return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

const RecentDemands = () => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 12;

  useEffect(() => {
    setLoading(true);
    demandAPI.publicGrouped()
      .then(r => {
        setCities(r.data.data || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(cities.length / limit);
  const displayedCities = cities.slice((page - 1) * limit, page * limit);

  return (
    <PublicLayout>
      <Seo 
        title="Recent Demands — Market Pulse" 
        description="Browse the latest product and service requirements posted by buyers on Kanjusee. Verified vendors are bidding in real-time."
      />
      
      <div style={{ background: C.cream, minHeight: '100vh', padding: '60px 24px' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto' }}>
          <div style={{ marginBottom: 40, textAlign: 'center' }}>
            <div style={{ fontFamily: F.body, fontSize: 12, fontWeight: 800, color: C.gold, textTransform: 'uppercase', letterSpacing: 3, marginBottom: 12 }}>Market Pulse</div>
            <h1 style={{ fontFamily: F.display, fontSize: 42, fontWeight: 900, color: C.charcoal, marginBottom: 16 }}>Recent Demands</h1>
            <p style={{ fontFamily: F.body, fontSize: 16, color: C.muted, maxWidth: 600, margin: '0 auto' }}>
              Real requirements from real buyers across India. This is where demand meets supply.
            </p>
          </div>

          {loading ? (
            <ProductListSkeleton count={6} columns={3} />
          ) : cities.length === 0 ? (
            <EmptyState
              title="No recent demands found"
              description="Check back later or post your own requirement to receive bids from verified vendors!"
              actionLabel="Post a Demand"
              onAction={() => window.location.href = '/post-demand'}
            />
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 24 }}>
                {displayedCities.map(cityData => (
                  <div key={cityData._id} style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 20, padding: 28, transition: 'all 0.2s', position: 'relative' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.green; e.currentTarget.style.boxShadow = '0 12px 30px rgba(27,92,58,0.08)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.boxShadow = 'none'; }}>
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MapPin size={22} color={C.green} style={{ flexShrink: 0 }} />
                        <h3 style={{ fontFamily: F.display, fontSize: 22, fontWeight: 900, color: C.charcoal, margin: 0 }}>
                          {capitalizeCity(cityData._id)}
                        </h3>
                      </div>
                      <div style={{ background: C.greenLight, color: C.green, padding: '5px 12px', borderRadius: 8, fontSize: 11, fontWeight: 800 }}>
                        {cityData.totalCount} Demand{cityData.totalCount > 1 ? 's' : ''}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gap: 10, minHeight: 120 }}>
                      {cityData.categories.map((cat, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.grayLight, borderRadius: 10 }}>
                          <span style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.charcoal }}>
                            {cat.name}
                          </span>
                          <span style={{ fontFamily: F.body, fontSize: 14, fontWeight: 800, color: C.green }}>
                            {cat.count} demand{cat.count > 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 50 }}>
                  <button 
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontWeight: 700, color: page === 1 ? C.border : C.green, cursor: page === 1 ? 'default' : 'pointer' }}>
                    ← Prev
                  </button>
                  <div style={{ fontFamily: F.body, fontSize: 14, fontWeight: 700, color: C.charcoal }}>
                    Page {page} of {totalPages}
                  </div>
                  <button 
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                    style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', fontWeight: 700, color: page === totalPages ? C.border : C.green, cursor: page === totalPages ? 'default' : 'pointer' }}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
          
          <div style={{ marginTop: 80, textAlign: 'center', background: `linear-gradient(135deg, ${C.greenDark}, ${C.green})`, borderRadius: 24, padding: '60px 40px', color: C.white }}>
            <h2 style={{ fontFamily: F.display, fontSize: 32, marginBottom: 16 }}>Need something similar?</h2>
            <p style={{ fontFamily: F.body, fontSize: 16, opacity: 0.8, marginBottom: 32, maxWidth: 500, margin: '0 auto 32px' }}>
              Post your own demand for free and let verified vendors across India compete to win your order at your budget.
            </p>
            <button onClick={() => window.location.href='/post-demand'} style={{ background: C.gold, color: C.charcoal, border: 'none', borderRadius: 12, padding: '16px 32px', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
              Post My Demand Now — Free
            </button>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default RecentDemands;
